import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Base stylesheet for the message document. Every rule is wrapped in
 * `:where()` so it carries zero specificity — the email's own <style> blocks,
 * inline styles and even bare element selectors all win over it. It only
 * supplies what an email client supplies when the sender didn't: Outlook's
 * reading font, a sane text colour, image clamping and quote styling.
 */
const FRAME_CSS = `
:where(html){overflow-y:hidden;overflow-x:auto;}
:where(body){margin:0;padding:2px 2px 12px;background:#fff;color:#242424;
  font-family:"Segoe UI","Segoe UI Web (West European)",-apple-system,BlinkMacSystemFont,Roboto,"Helvetica Neue",Arial,sans-serif;
  font-size:15px;line-height:1.45;overflow-wrap:break-word;-webkit-font-smoothing:antialiased;}
:where(a){color:#0f6cbd;}
:where(img){max-width:100%;}
:where(pre){white-space:pre-wrap;font-family:inherit;}
:where(blockquote){margin:0 0 0 .8ex;padding-left:1ex;border-left:2px solid #d1d1d1;color:#616161;}
:where(table){max-width:100%;}
`;

/**
 * With remote content blocked (sender not in Safe senders) the frame gets a
 * CSP that only allows images already inside the message (data:/blob:/cid:)
 * or served by webmail itself — remote images, tracking pixels, fonts and
 * stylesheets simply don't load, as in Outlook. Inline styles still apply.
 */
const BLOCK_REMOTE_CSP =
    "default-src 'none'; img-src 'self' data: blob: cid:; style-src 'unsafe-inline'; font-src data:; media-src 'none'";

function buildDocument(html: string, blockRemote: boolean): string {
    // - no-referrer: remote images and link clicks don't leak the webmail URL
    //   (tracking pixels learn nothing about where the mail was opened).
    // - base target=_blank: every link opens in a new tab instead of
    //   navigating the frame itself.
    // - color-scheme light: emails are authored for a white page; Outlook
    //   keeps them on white too unless the sender opts into dark.
    return `<!doctype html><html><head><meta charset="utf-8">`
        + (blockRemote ? `<meta http-equiv="Content-Security-Policy" content="${BLOCK_REMOTE_CSP}">` : "")
        + `<meta name="referrer" content="no-referrer">`
        + `<meta name="color-scheme" content="light">`
        + `<base target="_blank">`
        + `<style>${FRAME_CSS}</style></head><body>${html}</body></html>`;
}

/**
 * Renders an email's HTML in a sandboxed, auto-height iframe — the way Outlook
 * on the web and Gmail isolate message content.
 *
 * Rendering into the app DOM (the previous approach) had three problems:
 *  1. Tailwind's preflight reset stripped every email of its defaults —
 *     headings shrank to body size, paragraph spacing and list bullets
 *     vanished, table border attributes were zeroed, images became blocks.
 *     The `prose` classes meant to restore them did nothing: the typography
 *     plugin was never installed.
 *  2. An email's own <style> block applied to the WHOLE app.
 *  3. Inline event handlers (<img onerror=…>) executed in the app's origin.
 *
 * The sandbox omits `allow-scripts`, so nothing in the email ever runs.
 * `allow-same-origin` is only there so this component can measure the
 * document height and forward keyboard/mouse events to the app.
 */
export function EmailBodyFrame({
    html,
    className,
    blockRemote = false
}: {
    html: string;
    className?: string;
    /** Block remote images/fonts/styles (sender not in Safe senders). */
    blockRemote?: boolean;
}) {
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState(160);
    // Hidden until the document has rendered and been measured, then faded in — no empty white box between messages.
    const [ready, setReady] = useState(false);

    // Layout effect, not a passive one: the listener must be attached in the
    // same commit that sets srcDoc, before the frame's load task can fire.
    useLayoutEffect(() => {
        const frame = frameRef.current;
        if (!frame) return;
        let observer: ResizeObserver | null = null;
        let detach: (() => void) | null = null;

        const onLoad = () => {
            const doc = frame.contentDocument;
            if (!doc?.body) return;

            const measure = () => {
                const root = doc.documentElement;
                // Leave room for the horizontal scrollbar when a fixed-width
                // newsletter is wider than the pane.
                const scrollbar = root.scrollWidth > root.clientWidth ? 18 : 0;
                setHeight(Math.ceil(doc.body.scrollHeight) + scrollbar);
            };

            // A wide image scaled down by max-width keeps its height attribute
            // and ends up squashed; let those recompute height from the width.
            const fixImage = (img: HTMLImageElement) => {
                const declared = Number(img.getAttribute("width"));
                if (declared && img.clientWidth > 0 && img.clientWidth < declared) img.style.height = "auto";
            };
            doc.querySelectorAll("img").forEach((img) => {
                if (img.complete) fixImage(img);
                else img.addEventListener("load", () => { fixImage(img); measure(); }, { once: true });
            });

            // Focus moves into the frame on click, so without forwarding the
            // mail shortcuts (J/K, E, Delete…) would go dead and open popovers
            // would never see the outside click that closes them.
            const onKey = (e: KeyboardEvent) => {
                if (e.ctrlKey || e.metaKey || e.altKey) return;
                window.dispatchEvent(new KeyboardEvent("keydown", {
                    key: e.key,
                    code: e.code,
                    shiftKey: e.shiftKey,
                    bubbles: true,
                    cancelable: true
                }));
            };
            const onPointer = () => {
                document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
                document.body.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true }));
            };
            doc.addEventListener("keydown", onKey);
            doc.addEventListener("mousedown", onPointer);
            detach = () => {
                doc.removeEventListener("keydown", onKey);
                doc.removeEventListener("mousedown", onPointer);
            };

            measure();
            setReady(true);
            observer = new ResizeObserver(measure);
            observer.observe(doc.body);
        };

        setReady(false);
        // Never leave the body hidden if the load event is missed.
        const reveal = window.setTimeout(() => setReady(true), 800);
        frame.addEventListener("load", onLoad);
        return () => {
            window.clearTimeout(reveal);
            frame.removeEventListener("load", onLoad);
            observer?.disconnect();
            detach?.();
        };
    }, [html, blockRemote]);

    return (
        <iframe
            ref={frameRef}
            title="Message body"
            srcDoc={buildDocument(html, blockRemote)}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            className={cn("block w-full border-0 bg-white transition-opacity duration-150", ready ? "opacity-100" : "opacity-0", className)}
            style={{ height }}
        />
    );
}
